import {
    Alert,
    App,
    Button,
    DatePicker,
    Flex,
    Form,
    FormProps,
    Input,
    Modal,
    Select,
    Tooltip,
    Typography,
    Upload
} from "antd";
import { Campaign, CampaignDetail as ICampaignDetail, CreateAudience, PushActionType } from "../interfaces/Campaign.interface"
import {useEffect, useMemo, useState} from "react";
import { InfoCircleOutlined, PlusOutlined, UploadOutlined } from "@ant-design/icons"
import { RcFile, UploadChangeParam, UploadFile } from "antd/es/upload";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AudienceCreator } from "./AudienceCreator";
import dayjs from "dayjs";
import { useAuth } from "../hooks/useAuth";
import { tagRender } from "./InputHelpers/Tag.helper";
import { audienceQueryToSelectOptions } from "./InputHelpers/AudienceSeleciton.helper";
import { APIValidationError, ValidationError } from "../utils/api.validation.utils";
import './CampaignEditor.css'
import { getBase64 } from "../utils/file.utils";
import { Configure, ConfigureProps, InstantSearch } from 'react-instantsearch';
import { instantMeiliSearch } from '@meilisearch/instant-meilisearch';
import type { SearchClient } from 'instantsearch.js';
import 'instantsearch.css/themes/satellite.css';
import { Mode } from "../interfaces/mode.type";
import {SearchConfiguration} from "../interfaces/OrgDetail.interface";
import CustomInfiniteHits from "./InstantSearch/CustomInfiniteHits";
import {CustomSearchBox} from "./InstantSearch/CustomSearchBox";
import { DefaultOptionType } from "antd/es/select";

export interface CampaignEditorProps {

    applicationId: string

    searchConfig: SearchConfiguration | null;

    // pass the campaign to the editor
    campaign: Campaign | null;

    // called when + by the editor starts
    onEdit: (campaign: Campaign) => void;

    // called when + by the editor dismisses
    onDismiss: (campaign: Campaign) => void;

    // called when + by the editor when the user requests the campaign to be saved
    onSave: (campaign: Campaign) => Promise<ICampaignDetail|APIValidationError>

}

const convertImageToBase64 = async (imageUrl: string): Promise<string> => {
    const response = await fetch(imageUrl);
    const blob = await response.blob();

    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
    });
};


const isCampaignDetail = (value: unknown): value is ICampaignDetail => {
    if (!value || typeof value !== "object") {
        return false;
    }

    const candidate = value as Record<string, unknown>;
    return typeof candidate.id === "string" && "createdAt" in candidate;
};

const isAPIValidationError = (value: unknown): value is APIValidationError => {
    return (
        value instanceof APIValidationError ||
        (value !== null &&
            typeof value === "object" &&
            "response" in value &&
            "message" in (value as any).response)
    );
};

export const CampaignEditor = (props: CampaignEditorProps) => {
    const [campaign, setCampaign] = useState<Campaign | null>(props.campaign);
    const [confirmLoading, setConfirmLoading] = useState(false);
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [form] = Form.useForm<Campaign>();
    const queryClient = useQueryClient()
    const [fileList, setFileList] = useState<Array<any>>([]);
    const [imageData, setImageData] = useState<string>("");
    const [imageChanged, setImageChanged] = useState<boolean>(false); // Track if user changed the image
    const [originalImageData, setOriginalImageData] = useState<string>(""); // Store original hash/ID
    const { message } = App.useApp();
    const beforeUpload = (file: RcFile) => {
        const allowedTypes = ["image/png", "image/jpeg"];
        const isAllowed = allowedTypes.includes(file.type);
        if (!isAllowed) {
            message.error("Only PNG or JPEG images are supported for push notifications.");
            return Upload.LIST_IGNORE;
        }
        return false;
    };
    const auth = useAuth();
    const [ showSearchResults, setShowSearchResults ] = useState<boolean>(false)
    const [ searchError, setSearchError ] = useState<boolean>(false)
    const [previewVisible, setPreviewVisible] = useState(false);
    const [previewImage, setPreviewImage] = useState('');
    const [isSaving, setIsSaving] = useState(false);

    const clearImageErrors = () => {
        // Clear any server-side validation errors for the image field when the user changes the file
        form.setFields([
            { name: ["pushMessage", "imageData"], errors: [] }
        ]);
    };

    const hasSearch = useMemo(() => {
        const config = props.searchConfig;
        if (config === null) {
            return false;
        }

        return config?.headendBaseUrl !== '' && config?.searchBaseUrl !== '' && config?.deeplinkBaseUrl !== '';
    }, [ props.searchConfig ]);

    // reset on campaign change
    useEffect(() => {
        // Don't reset during save operation
        if (isSaving) return;

        // If the user has changed the image and we're still on the same campaign,
        // keep their pending upload visible even if a validation error occurred.
        if (imageChanged && props.campaign?.id === campaign?.id) {
            return;
        }
        
        const imageId = props.campaign?.pushMessage.imageId;
        const imageData = props.campaign?.pushMessage.imageData; // base64 only if clone fetched it
        const isBase64 = !!imageData && imageData.startsWith('data:');
        const mimeMatch = imageData?.match(/^data:(image\/[a-zA-Z0-9.+-]+);base64,/);
        const base64Ext = mimeMatch ? mimeMatch[1].replace("image/", "") : "png";
        const sourceId = props.campaign?.id ?? (props.campaign as any)?.clonedFromId;
        const fileUid = `${sourceId ?? imageId ?? 'image'}`;

        setOriginalImageData(imageId ?? "");
        
        if (isBase64) {
            setFileList([
                {
                    uid: fileUid,
                    name: `image-${props.applicationId}-${sourceId ?? ""}.${base64Ext}`,
                    status: "done",
                    url: imageData,
                    thumbUrl: imageData
                }
            ])
            setImageData(imageData)
        } else if (sourceId && imageId) {
            const imageUrl = `/api/v1/app/${props.applicationId}/campaign/${sourceId}/image`;
            setFileList([
                {
                    uid: fileUid,
                    name: `image-${props.applicationId}-${sourceId}.png`,
                    status: "done",
                    url: imageUrl,
                    thumbUrl: imageUrl
                }
            ]);
            setImageData('');
        } else {
            setFileList([]);
            setImageData('');
        }
        // Reset the imageChanged flag when loading a campaign
        setImageChanged(false);
    }, [
        props.campaign?.id,
        (props.campaign as any)?.clonedFromId,
        props.campaign?.pushMessage.imageId,
        props.campaign?.pushMessage.imageData,
        isSaving,
        imageChanged,
        campaign?.id
    ]);

    useEffect(() => {
        setCampaign(props.campaign)
        if (props.campaign) {
            form.setFieldsValue({...props.campaign})
        } else {
            // When campaign is null (dismissed), reset everything
            form.resetFields();
            setImageData('');
            setFileList([]);
            setImageChanged(false);
        }
    }, [form, props.campaign])

    useEffect(() => {
        setIsOpen(campaign != null)
    }, [campaign])

    const audienceQuery = useQuery({ queryKey: ['audience', props.applicationId], queryFn: () => auth.api.getAudiences(props.applicationId) })

    const audienceMutation = useMutation({
        mutationFn: async (audience: CreateAudience) => await auth.api.createAudience(props.applicationId, audience),
        onSuccess: () => {
            // Invalidate and refetch
            queryClient.invalidateQueries({ queryKey: ['audience', props.applicationId] })
        },
        onError: () => {
            console.error("Oops we failed to create an Audience")
            message.error("Oops we failed to create an Audience");
        }
    })
    const createCampaign = () => {
        props.onEdit({
            name: "",
            pushMessage: {
                title: "",
                message: "",
                imageData: "",
                actions: [{ type: PushActionType.open.toString(), url: ""}],
                isSilent: false
            },
            audiences: [],
            deliverAt: new Date(),
            isTestCampaign: false,
            meta:{}
        } as Campaign)
    }

    const handleSave = async () => {
        try {
            await form.validateFields();
        } catch (err)
        {
            console.log("validation failed", err);
            return;
        }

        let data = form.getFieldsValue();
        
        // Determine what to send:
        // - If user changed the image, send imageData (base64) and no imageId
        // - If unchanged and we have the original imageId, send imageId and no imageData
        // - Clear checks: can check for nullable/undefined to know which is set
        let imageIdToSend: string | undefined = undefined;
        let imageDataToSend: string | undefined = undefined;
        
        if (imageChanged && imageData?.length > 0) {
            // New image uploaded - send base64 data
            imageDataToSend = imageData;
        } else if (!imageChanged && originalImageData) {
            // Reuse existing image - send imageId
            imageIdToSend = originalImageData;
        }
        
        let payload:Campaign = {
            name: data.name,
            pushMessage: {
                title: data.pushMessage.title ?? "",
                message: data.pushMessage.message ?? "",
                imageId: imageIdToSend,
                imageData: imageDataToSend,
                isSilent: false,
                actions: (data.pushMessage?.actions ?? []).map(x=>{
                    x.type = PushActionType.open;
                    return x;
                })
            },
            audiences: data.audiences,
            deliverAt: new Date(data.deliverAt).toISOString(),
            isTestCampaign: false,
            meta: {}
        };

        if (payload) {
            setConfirmLoading(true);
            setIsSaving(true);
            let result: unknown;
            try {
                result = await props.onSave(payload)
            } catch (err) {
                result = err;
            }
            // console.log("result", result,'id' in result && 'createdAt' in result);
            if (isCampaignDetail(result)) {
                // First notify parent to clear campaign state
                if (campaign) {
                    props.onDismiss(campaign);
                }
                // Then clear local state and close modal
                setImageData('');
                setFileList([]);
                setImageChanged(false);
                setShowSearchResults(false);
                setSearchError(false);
                setIsOpen(false);
                form.resetFields();
                setIsSaving(false);
            } else if (isAPIValidationError(result)) {
                const error = result;
                const hasFieldErrors = Array.isArray(error?.response?.context) && (error.response?.context ?? []).length > 0;

                if (hasFieldErrors) {
                    let fieldData:any[] = (error.response?.context ?? []).reduce((prev:ValidationError[], next:ValidationError)=>{
                        prev.push(next)
                        if(next.children.length > 0)
                        {
                            prev.push(...next.children.map((x:any)=>{
                                x.property = [next.property,x.property]
                                return x;
                            }))
                        }
                        return prev;
                    }, []).map((e:any)=>{
                        return {
                            name:e.property,
                            errors: Object.entries(e.constraints ?? {}).map(c=>c[1])
                        }
                    }).filter((x:any)=>x.errors.length > 0)
                    // do something on failure
                    console.log("tell form about error", error,fieldData);
                    form.setFields(fieldData)
                }

                const errorMessage =
                    error?.response?.message
                    || 'Failed to save campaign';

                // Only show toast when there are no field-level errors
                if (!hasFieldErrors && errorMessage) {
                    message.error(errorMessage);
                }
                setIsSaving(false);
            } else {
                message.error('Failed to save campaign');
                setIsSaving(false);
            }
            setConfirmLoading(false);
        }
    }

    const handleCancel = () => {
        setImageData('')
        setShowSearchResults(false)
        setSearchError(false)
        setFileList([])
        setImageChanged(false);

        if (campaign) {
            props.onDismiss(campaign)
        }
        form.resetFields();
    }

    const onFormValueChange = (formValue: any) => {
        // console.log('formValue', formValue)
    };

    const onFinish: FormProps<any>['onFinish'] = (values) => {
        // console.log('Success:', values);
    };


    const handleUploadChange = async (event: UploadChangeParam<UploadFile<any>>) => {
        const nextFiles = (event.fileList ?? []).slice(-1);
        setFileList(nextFiles);
        clearImageErrors();

        if (nextFiles.length === 0) {
            setImageData('');
            return;
        }

        const originFile = nextFiles[0]?.originFileObj;
        if (originFile) {
            let dataUrl = await getBase64(originFile as Blob).then(x => x?.toString() ?? "");
            setImageData(dataUrl);
            setImageChanged(true); // Mark that the user changed the image
        }
    }

    const createAudience = async (audience: CreateAudience) => {
        try {
            let newAudience = await audienceMutation.mutateAsync(audience);
            if(newAudience?.id)
            {
                let currentAudiences = form.getFieldValue("audiences") as string[]
                currentAudiences.push(newAudience.id);
                form.setFieldValue("audiences", currentAudiences);
            }
            return newAudience;
        } catch (error) {
            // Re-throw the error so it's caught by the AudienceCreator component
            throw error;
        }
    }

    let searchClient: SearchClient | null = null;
    let searchIndexName = props.searchConfig?.searchIndexName || 'vodAsset';
    if (hasSearch) {
        const meiliSearch = instantMeiliSearch(
            props.searchConfig!.searchBaseUrl,
            props.searchConfig?.searchApiKey || ''
        ) as unknown as { searchClient: SearchClient };

        const defaultSearchClient = meiliSearch.searchClient

        searchClient = {
            ...defaultSearchClient,
            search(requests: any): Promise<any> {
                return defaultSearchClient.search(requests).catch((error) => {
                    console.error("search error: ", error)

                    setSearchError(true);

                    // Return empty results to prevent crash
                    return {
                        results: requests.map(() => ({
                            hits: [],
                            nbHits: 0,
                            processingTimeMs: 0,
                            query: '',
                        })),
                    };
                })
            }
        }
    }

    interface hit {
        id: number,
        siteId: number,
        type: "TV" | "MOVIE" | "SEASON" | "SERIES",
        programmeId: string,
        showId: string,
        showTitle: string,
        title: string,
        seasonNumber: number,
        episodeNumber: number
    }

    const handlePreview = async (file: any) => {
        if (!file.url && !file.preview) {
            file.preview = await getBase64(file.originFileObj);
        }

        setPreviewImage(file.url || file.preview);
        setPreviewVisible(true);
    };

    return (
        <>
            <Button type="primary" onClick={createCampaign}>
                <PlusOutlined /> New Campaign
            </Button>
            {isOpen && <Modal
                open={isOpen}
                onOk={handleSave}
                okText={"Save"}
                confirmLoading={confirmLoading}
                onCancel={handleCancel}
                width={`99vw`}
                destroyOnClose
            >
                <h1>{"Campaign"}</h1>
                <Form
                    preserve={false}
                    layout={"vertical"}
                    form={form}
                    noValidate={false}
                    initialValues={{ ...campaign }}
                    onValuesChange={onFormValueChange}
                    onFinish={onFinish}
                >
                    <Flex vertical={false} gap={10} style={{width: '100%'}}>
                        <Flex vertical={true} style={{width: '100%'}} >

                            <Form.Item label="Campaign Name"
                                name={["name"]}
                                hasFeedback
                                validateTrigger={"onChange"}
                                rules={[
                                    { required: true,  message: "Campaign 'name' is required"},
                                    { min: 3, message: "Campaign 'name' needs to be at least 3 characters" }
                                ]}>
                                <Input placeholder="New Content!" />
                            </Form.Item>

                            <Form.Item label="Title"
                                name={["pushMessage", "title"]}
                                validateTrigger={"onChange"}
                                hasFeedback
                                rules={[
                                    { required: true,  message: "Push 'title' is required"},
                                    { min: 3, message: "Push message 'title' needs to be at least 3 characters" },
                                ]}>
                                <Input placeholder="New Content!" />
                            </Form.Item>
                            <Form.Item label="Message"
                                name={["pushMessage", "message"]}
                                hasFeedback
                                rules={[{ required: true, message: "Push 'message' is required " }]}>
                                <Input placeholder="A New Episode of House of the Dragon is Available" />
                            </Form.Item>
                            <Form.Item hidden
                                name={["pushMessage","actions", 0,"type"]} >
                                    <Input></Input>
                            </Form.Item>

                            {hasSearch && searchClient &&
                            <InstantSearch
                                indexName={searchIndexName}
                                searchClient={searchClient as unknown as SearchClient}
                            >
                                {/* Forcing this through type-checking as 'filters' is supported by instant-meilisearch, (https://github.com/meilisearch/meilisearch-js-plugins/issues/389)
                                  * but it does not seem to get added to the React component typedefs correctly  
                                  */}
                                <Configure {...{ filters: "state=PENDING OR state=APPROVED" } as ConfigureProps } />
                                <Form.Item label='Search (Optional)' id='SearchInputContainer'>
                                    {searchError &&
                                        <Alert
                                            type='error'
                                            message='Search is not currently available. Please try again or contact support if the issue persists'
                                        />
                                    }
                                    {!searchError &&
                                        <CustomSearchBox
                                            onChange={(query: string) => setShowSearchResults(query.length > 0)}
                                        />
                                    }
                                </Form.Item>
                                {showSearchResults && <CustomInfiniteHits
                                    searchConfig={props.searchConfig as SearchConfiguration}
                                    onHitSelected={(hit, deeplink, imageUrl) => {
                                        setShowSearchResults(false)

                                        form.setFields([{
                                            name: ["pushMessage", "actions", 0, "url"],
                                            value: deeplink,
                                            errors: []
                                        }])

                                        convertImageToBase64(imageUrl).then(dataUrl => {
                                            setImageData(dataUrl)
                                            setImageChanged(true); // Mark image as user-chosen so it is sent on save

                                            setFileList([{
                                                uid: "-1",
                                                name: `${hit.id}_landscape`,
                                                status: "done",
                                                url: dataUrl
                                            }])
                                            clearImageErrors();
                                        })
                                    }}
                                />}
                            </InstantSearch>
                            }

                            <Form.Item label="Deeplink"
                                name={["pushMessage","actions", 0,"url"]}
                                rules={[
                                    { required: true,  message: "Push 'deeplink' is required" }, 
                                    { type: 'url', warningOnly: false, message: "Push 'deeplink' needs to be a valid url" }
                                ]}>
                                <Input placeholder="https://watch.your-streaming-service.com" />
                            </Form.Item>
                            
                            <Form.Item style={{ position: 'relative' }} label="Image" name={["pushMessage", "imageData"]}
                                       getValueFromEvent={(event) => {
                                           console.log("image:getValueFromEvent", event);
                                           if (event.fileList && event.fileList.length > 0) {
                                               return event.fileList[0];
                                           }
                                           return "";
                                       }}
                            >
                                <Tooltip
                                            title={
                                                <div>
                                                    <div>Format: PNG or JPEG</div>
                                                    <div>Max file size: 5MB</div>
                                                    <div>Max resolution: 1920px on the longest side</div>
                                                    <div>Note: Images larger than ~300KB will be automatically resized for optimal delivery</div>
                                                </div>
                                            }
                                        >
                                            {/* Position the info icon relative to the Form.Item */}
                                            <span style={{ position: 'absolute', top: '-28px', left: '48px', color: '#999' }}>
                                                <InfoCircleOutlined />
                                            </span>
                                        </Tooltip>
                                <Upload
                                    accept="image/png,image/jpeg"
                                    beforeUpload={beforeUpload}
                                    onChange={handleUploadChange}
                                    onRemove={() => {
                                        setFileList([]);
                                    setImageData('');
                                    setImageChanged(true);
                                    clearImageErrors();
                                    }}
                                    fileList={fileList}
                                    multiple={false}
                                    maxCount={1}
                                    listType="picture-card"
                                    // Use custom preview handling because the default results in errors from
                                    // trying to navigate to data urls
                                    onPreview={handlePreview}
                                >
                                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                        <UploadOutlined />
                                        
                                    </div>
                                </Upload>

                                <Modal
                                    open={previewVisible}
                                    footer={null}
                                    onCancel={() => setPreviewVisible(false)}
                                >
                                    <img alt="preview" style={{ width: '100%' }} src={previewImage} />
                                </Modal>
                            </Form.Item>

                            <Form.Item className="full_width" label={<Flex align="baseline" gap={5} justify="space-between" style={{ width: '100%'}}>
                                <Typography.Text>Segments</Typography.Text>
                                <AudienceCreator operationMode={Mode.New} applicationId={props.applicationId} saveAudience={(audience: CreateAudience) => createAudience(audience)} />
                            </Flex>
                            } name="audiences" getValueFromEvent={(e) => {
                                return e;
                            }}
                                rules={[
                                    { type: "array", min: 1 , message: "At least one Audience Segment is needed for targeting of a Push Message", required: true}, 
                                ]}
                            >
                                <Select
                                    mode="multiple"
                                    tagRender={tagRender}
                                    style={{ width: '100%' }}
                                    filterOption={(inputValue: string, option?: DefaultOptionType) => {
                                        return (option?.label ?? "").toString().toLowerCase().includes((inputValue??"").toLowerCase())
                                    }}
                                    options={audienceQueryToSelectOptions(audienceQuery)}
                                />
                            </Form.Item>
                            <Form.Item label="Deliver At" name={["deliverAt"]}
                                rules={[
                                    {type: 'date', required: true, message: "Please specify a delivery date and time."}
                                ]}
                                getValueFromEvent={(e) => e.toISOString()}
                                getValueProps={(value:dayjs.Dayjs) => {
                                    return ({ value: value ? dayjs(value) : "" })
                                }}
                                >
                                <DatePicker showTime />
                            </Form.Item>


                        </Flex>
                        {/* <Flex vertical style={{ maxWidth: '50%' }}>
                            <NotificationPreview title={form.getFieldValue('title')} message="" imageUrl="" deepLinkUrl="" type={"safari-mac"} ></NotificationPreview>
                        </Flex> */}
                        
                    </Flex>
                    {/* <pre>
                        {JSON.stringify(campaign, null, 5)}
                    </pre> */}
                </Form>
            </Modal>}
        </>
    )
}
